/**
 * Page plein écran de transcription d'un appel Aircall.
 * Route : /admin/aircall/calls/:callLogId
 *
 * Affiche en grand format :
 *   - Header avec contexte (prospect, durée, direction, date)
 *   - Audio player wide
 *   - Résumé IA + outcome + sentiment
 *   - Key moments cliquables (seek)
 *   - Transcription complète intégrale (segments avec timestamps)
 *   - Actions à faire
 *   - Coût estimé Whisper
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Loader2, ArrowLeft, Sparkles, Mic, Play, Pause, AlertTriangle, CheckCircle2,
  DollarSign, Clock, User as UserIcon, TrendingUp, Lightbulb, RefreshCw, ExternalLink, Euro,
} from "lucide-react";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";

const KEY_MOMENT_META = {
  intent:     { label: "Signal d'achat", icon: TrendingUp, color: "#22c55e" },
  objection:  { label: "Objection",      icon: AlertTriangle, color: "#f43f5e" },
  prix:       { label: "Prix / budget",  icon: DollarSign, color: "#f59e0b" },
  engagement: { label: "Engagement",     icon: CheckCircle2, color: "#10b981" },
  decideur:   { label: "Décideur",       icon: UserIcon, color: "#8b5cf6" },
  delai:      { label: "Délai",          icon: Clock, color: "#3b82f6" },
  concurrent: { label: "Concurrent",     icon: AlertTriangle, color: "#f97316" },
  info_clef:  { label: "Info clé",       icon: Lightbulb, color: "#06b6d4" },
};

const SENTIMENT_BADGE = {
  positif: { color: "#22c55e", label: "Positif", icon: "😊" },
  neutre:  { color: "#64748b", label: "Neutre", icon: "😐" },
  negatif: { color: "#f43f5e", label: "Négatif", icon: "😟" },
};

const formatTime = (s) => {
  if (s == null) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export default function CallTranscriptionPage() {
  const { callLogId } = useParams();
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transcribing, setTranscribing] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/aircall/calls/${callLogId}/transcription`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (callLogId) reload(); /* eslint-disable-next-line */ }, [callLogId]);

  const transcribe = async () => {
    setTranscribing(true);
    try {
      await api.post(`/aircall/calls/${callLogId}/transcribe`);
      await reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur de transcription.");
    } finally {
      setTranscribing(false);
    }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      await api.post(`/aircall/calls/${callLogId}/transcribe/regenerate-analysis`);
      await reload();
    } finally {
      setReanalyzing(false);
    }
  };

  const seekTo = (seconds) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, seconds);
    audioRef.current.play().catch(() => {});
    setPlaying(true);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <PageHeader title="Appel introuvable" subtitle="Cet enregistrement n'existe pas ou n'est plus accessible." />
        <Link to="/admin/leads-chauds" className="text-violet-400 hover:underline text-sm">
          <ArrowLeft className="w-3 h-3 inline mr-1" /> Retour
        </Link>
      </div>
    );
  }

  const transcription = data.transcription;
  const analysis = transcription?.analysis;
  const sentimentCfg = SENTIMENT_BADGE[analysis?.sentiment] || SENTIMENT_BADGE.neutre;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="call-transcription-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin/leads-chauds" className="text-xs text-violet-400 hover:underline inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Retour aux leads chauds
          </Link>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <Mic className="w-7 h-7 text-violet-400" />
            Transcription IA de l'appel
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold mr-2 ${data.direction === "inbound" ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`}>
              {data.direction === "inbound" ? "ENTRANT" : "SORTANT"}
            </span>
            {data.phone && <span className="font-mono">{data.phone}</span>}
            {data.duration ? <span className="ml-2">{data.duration}s</span> : null}
            {data.created_at && <span className="ml-2">· {data.created_at?.slice(0, 16).replace("T", " ")}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.has_transcription && (
            <Button onClick={reanalyze} disabled={reanalyzing} variant="outline" size="sm" className="text-xs" data-testid="btn-reanalyze-fullscreen">
              {reanalyzing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Réanalyser
            </Button>
          )}
        </div>
      </div>

      {/* Pas encore transcrit */}
      {!data.has_transcription && (
        <div className="bg-[#0b0811] rounded-2xl border border-violet-500/20 p-8 text-center" data-testid="empty-fullscreen">
          {!data.has_recording ? (
            <p className="text-slate-400">Aucun enregistrement disponible pour cet appel.</p>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-violet-500/15 flex items-center justify-center mx-auto mb-4">
                <Mic className="w-8 h-8 text-violet-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Transcription IA disponible</h3>
              <p className="text-sm text-slate-400 mb-4">Whisper-1 + détection de moments-clés Gemini.<br />Délai estimé : 30-60s selon la durée.</p>
              <Button onClick={transcribe} disabled={transcribing} className="bg-violet-500 hover:bg-violet-600 text-white" data-testid="btn-transcribe-fullscreen">
                {transcribing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {transcribing ? "Transcription en cours…" : "Transcrire avec IA"}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Transcription complète */}
      {data.has_transcription && (
        <>
          {/* Bandeau résumé + outcome + sentiment */}
          <div className="bg-gradient-to-br from-[#1a1325] to-[#0b0811] rounded-2xl border border-violet-500/20 p-5 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              {analysis?.outcome && (
                <span className="text-xs px-2.5 py-1 rounded-md bg-violet-500/20 text-violet-200 font-bold uppercase">
                  {analysis.outcome.replace(/_/g, " ")}
                </span>
              )}
              <span className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ background: sentimentCfg.color + "22", color: sentimentCfg.color }}>
                {sentimentCfg.icon} {sentimentCfg.label}
              </span>
              <span className="text-[11px] text-slate-500 ml-auto inline-flex items-center gap-1">
                <Euro className="w-3 h-3" />
                Coût Whisper : {Number(transcription?.estimated_cost_eur || 0).toFixed(4)} €
              </span>
            </div>
            {analysis?.summary && (
              <p className="text-base text-slate-200 leading-relaxed italic" data-testid="ai-summary-fullscreen">
                « {analysis.summary} »
              </p>
            )}
            <div className="text-[11px] text-slate-500">
              {transcription?.segments?.length || 0} segments · {formatTime(transcription?.duration)} ·{" "}
              {analysis?.source === "fallback" ? "⚙️ heuristique" : `🤖 ${transcription?.model || "Whisper"} + ${analysis?.model || "Gemini"}`}
            </div>
          </div>

          {/* Audio player wide */}
          {data.recording_url && (
            <div className="bg-[#0b0811] rounded-2xl border border-white/5 p-4">
              <div className="flex items-center gap-3">
                <button onClick={togglePlay} className="w-12 h-12 rounded-full bg-violet-500 hover:bg-violet-600 text-white flex items-center justify-center" data-testid="audio-toggle-fullscreen">
                  {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>
                <audio
                  ref={audioRef}
                  src={data.recording_url}
                  onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  controls
                  className="w-full h-10"
                />
                <a href={data.recording_url} target="_blank" rel="noreferrer" className="text-xs text-violet-300 hover:text-violet-200 inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Ouvrir
                </a>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Key moments */}
            {analysis?.key_moments?.length > 0 && (
              <div className="lg:col-span-2 bg-[#0b0811] rounded-2xl border border-white/5 p-5" data-testid="key-moments-fullscreen">
                <h3 className="text-xs font-bold uppercase tracking-wider text-violet-400 mb-3">
                  Moments clés ({analysis.key_moments.length})
                </h3>
                <div className="space-y-2">
                  {analysis.key_moments.map((km, i) => {
                    const meta = KEY_MOMENT_META[km.type] || KEY_MOMENT_META.info_clef;
                    const Icon = meta.icon;
                    return (
                      <button
                        key={i}
                        onClick={() => seekTo(km.timestamp_start)}
                        className="w-full text-left bg-[#14101c] hover:bg-white/5 rounded-xl p-3 transition-all"
                        data-testid={`fs-key-moment-${i}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: meta.color + "22" }}>
                            <Icon className="w-4 h-4" style={{ color: meta.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400 font-mono">{formatTime(km.timestamp_start)}</span>
                              {km.importance === "high" && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 font-bold">⚡ critique</span>
                              )}
                            </div>
                            <div className="text-sm text-white mt-0.5 leading-relaxed">{km.text}</div>
                            {km.why && <div className="text-xs text-slate-500 italic mt-1">{km.why}</div>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Next actions */}
            {analysis?.next_actions?.length > 0 && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-5" data-testid="actions-fullscreen">
                <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-3 flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Actions à faire
                </h3>
                <ul className="space-y-2">
                  {analysis.next_actions.map((a, i) => (
                    <li key={i} className="text-sm text-emerald-100 flex items-start gap-2">
                      <span className="text-emerald-400 flex-shrink-0 mt-0.5">→</span>
                      <span className="leading-relaxed">{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Transcription complète */}
          {transcription?.segments?.length > 0 && (
            <div className="bg-[#0b0811] rounded-2xl border border-white/5 p-5" data-testid="transcript-fullscreen">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                Transcription complète ({transcription.segments.length} segments)
              </h3>
              <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-2">
                {transcription.segments.map((seg, i) => {
                  const isCurrent = currentTime >= seg.start && currentTime <= seg.end;
                  return (
                    <button
                      key={i}
                      onClick={() => seekTo(seg.start)}
                      className={`w-full text-left flex items-start gap-3 text-sm leading-relaxed py-1.5 px-2 rounded-lg transition-colors ${isCurrent ? "bg-violet-500/15 text-white" : "text-slate-300 hover:bg-white/5"}`}
                      data-testid={`fs-segment-${i}`}
                    >
                      <span className="text-[11px] text-slate-500 font-mono flex-shrink-0 mt-0.5 w-12">
                        {formatTime(seg.start)}
                      </span>
                      <span className="flex-1">{seg.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
